import { useMemo } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from '@/shared/hooks/useLocation';
import { useToast } from '@/shared/ui/Toast';
import { describeCallsError } from '../utils/apiErrorMessage';
import { callsApi } from '../api/callsApi';
import type { HandledOutcome, QueueStatus } from '../api/callsApi';
import { callsKeys } from '../queryKeys';

// `days` = only calls from the last N days (null = all time). The cutoff is
// fixed when the filter is chosen (not per render) so the query key is stable.
export function useCallLog(
  filters: { agentId?: string; days?: number | null; openCallbacks?: boolean } = {},
) {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  const { agentId, days, openCallbacks } = filters;
  // The cutoff is fixed once per filter choice (rounded to the minute so
  // remounts share the cache) and is part of the key. Recomputing it on every
  // page fetch shifted the window between pages, so offset paging skipped or
  // repeated calls.
  const since = useMemo(
    () =>
      days != null
        ? new Date(
            Math.floor((Date.now() - days * 24 * 60 * 60 * 1000) / 60_000) * 60_000,
          ).toISOString()
        : undefined,
    [days],
  );

  const query = useInfiniteQuery({
    queryKey: callsKeys.logFiltered(locationId, { agentId, since, openCallbacks }),
    queryFn: ({ pageParam }) =>
      callsApi.listCallLog(locationId, pageParam as number, { agentId, openCallbacks, since }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.meta.page < lastPage.meta.totalPages ? lastPage.meta.page + 1 : undefined,
    enabled: !!locationId,
  });

  const calls = query.data?.pages.flatMap((p) => p.data) ?? [];
  const total = query.data?.pages[0]?.meta.total ?? 0;
  return { ...query, calls, total, locationId };
}

// Callback-queue actions: mark a captured callback in progress / handled.
// Handled outcomes (booked / not booked / not reached) power honest conversion stats.
export function useCallQueueActions() {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  const queryClient = useQueryClient();
  const toast = useToast();

  const updateQueue = useMutation({
    mutationFn: ({
      id,
      queueStatus,
      handledOutcome,
    }: {
      id: string;
      queueStatus?: QueueStatus;
      handledOutcome?: HandledOutcome | null;
    }) => callsApi.updateCallQueue(id, locationId, { queueStatus, handledOutcome }),
    onSuccess: () => {
      toast.success('Callback updated');
      queryClient.invalidateQueries({ queryKey: callsKeys.log(locationId) });
      queryClient.invalidateQueries({ queryKey: callsKeys.stats(locationId) });
      queryClient.invalidateQueries({ queryKey: callsKeys.attribution(locationId) });
    },
    onError: (err: Error) => toast.error(describeCallsError(err, 'Failed to update callback')),
  });

  return { updateQueue };
}
